"use client";

import React, { useState, useEffect } from "react";
import { useTranslations, useLocale } from "next-intl";
import { useParams, useRouter } from "next/navigation";
import Link from "next/link";
import { makeLocalizedHrefNoLocale } from "@/lib/localizedRoutes/routeHelpers";
import {
  MdRemoveRedEye,
  MdShield,
  MdAccountBalanceWallet,
  MdCheckCircle,
  MdCreditCard,
  MdAutorenew,
  MdErrorOutline,
} from "react-icons/md";
import styles from "./publish-listing.module.css";
import { ListingTypeEnum, UserTypes } from "@/types/types";
import ListingCardPremium from "@/app/[locale]/(client)/search-old/[...slugs]/components/listing-cards/listing-card-premium";
import ListingCard from "@/app/[locale]/(client)/search-old/[...slugs]/components/listing-cards/listing-card";
import PreviewFrame from "./components/preview-frame";
import { PublishingDetails } from "./components/types";
import { useAppLocale } from "@/hooks/useAppLocale";

interface PublishListingProps {
  listingId: string;
  stripeSuccessUrl: string;
  agencySuccessUrl: string;
  cancelUrl: string;
}

export default function PublishListingComponent({
  listingId,
  stripeSuccessUrl,
  agencySuccessUrl,
  cancelUrl,
}: PublishListingProps) {
  const allParams = useParams();
  const t = useTranslations("PaymentForm");
  const locale = useAppLocale();
  const router = useRouter();

  // --- STORE ---

  // --- LOCAL STATE ---
  const [details, setDetails] = useState<PublishingDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  console.log(listingId);
  // 1. Fetch Publishing Details from Resolver
  useEffect(() => {
    async function fetchDetails() {
      try {
        const res = await fetch(
          `/api/payments/getPublishingPage/${listingId}?locale=${locale}`,
        );
        if (!res.ok) throw new Error("Failed to load publishing details");
        const data = await res.json();
        setDetails(data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchDetails();
  }, [listingId, locale]);

  if (loading) {
    return <PublishListingLoading />;
  }

  if (error || !details)
    return (
      <div className={styles.errorState}>{error || "Listing not found"}</div>
    );

  const isAgent = details.user_type === UserTypes.AGENT;
  const cardData = details.listing_card;
  const isPremiumTier =
    details.tier === ListingTypeEnum.PRIME ||
    details.tier === ListingTypeEnum.SPOTLIGHT;
  console.log(cardData.has_web_tour, cardData.web_tour_url);
  return (
    <main className={styles.mainContainer}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t("title")}</h1>
        <p className={styles.description}>{t("description")}</p>
      </header>

      <div className={styles.layoutGrid}>
        {/* LEFT COLUMN: REAL PREVIEW */}
        <div className={styles.leftColumn}>
          <div className={styles.cardPreviewWrapper}>
            {isPremiumTier ? (
              <ListingCardPremium
                data={cardData}
                isResizable={false}
                hasActions={false}
                hasRoundButtons={false}
              />
            ) : (
              <ListingCard
                data={cardData}
                isResizable={false}
                hasActions={false}
                hasRoundButtons={false}
              />
            )}
          </div>

          <button
            className={styles.previewButton}
            onClick={() => setIsPreviewOpen(true)}
          >
            <MdRemoveRedEye size={20} />
            {t("livePreview") || "Live Preview"}
          </button>
        </div>

        {/* RIGHT COLUMN: ORDER SUMMARY */}
        <div className={styles.rightColumn}>
          <div className={styles.checkoutCard}>
            <h3 className={styles.checkoutTitle}>Order Summary</h3>

            <ul className={styles.summaryList}>
              <li className={styles.summaryItem}>
                <div className={styles.summaryLabel}>
                  <span className={styles.summaryIcon}>
                    <MdCheckCircle />
                  </span>
                  Plan: {details.tier.toUpperCase()}
                </div>
              </li>
              <li className={styles.summaryItem}>
                <div className={styles.summaryLabel}>
                  <span className={styles.summaryIcon}>
                    <MdCheckCircle />
                  </span>
                  Duration: 30 Days
                </div>
              </li>
              {/* LAUNCH DISCOUNT BADGE */}
              <li className={`${styles.summaryItem} ${styles.discountItem}`}>
                <div className={styles.summaryLabel}>
                  <span className={styles.summaryIcon}>
                    <MdAutorenew />
                  </span>
                  Discount
                </div>
                <div className={styles.discountBadge}>-50% Applied</div>
              </li>
            </ul>

            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Total to pay</span>
              <span className={styles.totalAmount}>
                {details.credit_cost} {isAgent ? "Credits" : "EUR"}
              </span>
            </div>

            {isAgent ? (
              <AgencyPaymentBox
                details={details}
                listingId={listingId}
                t={t}
                locale={locale}
                agencySuccessUrl={agencySuccessUrl}
              />
            ) : (
              <PrivatePaymentBox
                details={details}
                listingId={listingId}
                t={t}
                locale={locale}
                stripeSuccessUrl={stripeSuccessUrl}
                cancelUrl={cancelUrl}
              />
            )}

            <div className={styles.secureNotice}>
              <MdShield size={16} />
              <span>Secure encrypted transaction</span>
            </div>
          </div>
        </div>
        {isPreviewOpen && (
          <PreviewFrame
            listingId={cardData.listing_id}
            onClose={() => setIsPreviewOpen(false)}
            isXs={false}
            isSm={false}
            isMd={false}
            isLg={true}
          />
        )}
      </div>
    </main>
  );
}

// --- SUB-COMPONENTS ---

function AgencyPaymentBox({
  details,
  listingId,
  t,
  locale,
  agencySuccessUrl,
}: any) {
  const [checked, setChecked] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const handlePublish = async () => {
    if (!checked) return alert("Please agree to the terms");
    setIsPublishing(true);
    try {
      const res = await fetch("/api/payments/credits/activate-listing", {
        method: "POST",
        body: JSON.stringify({ listing_id: listingId }),
      });
      if (res.ok) window.location.href = agencySuccessUrl;
      else alert("Transaction failed. Check wallet balance.");
    } finally {
      localStorage.removeItem("pending_listing_draft");
      setIsPublishing(false);
    }
  };

  console.log(details.credit_cost, details.wallet_balance);
  return (
    <div className={styles.paymentBox}>
      <div className={styles.walletDisplay}>
        <div className={styles.walletIconWrapper}>
          <MdAccountBalanceWallet size={24} />
        </div>
        <div className={styles.walletText}>
          <span className={styles.walletLabel}>Agency Wallet</span>
          <span className={styles.walletBalance}>
            {details.wallet_balance} Credits
          </span>
        </div>
      </div>

      <TermsAndBilling
        checked={checked}
        setChecked={setChecked}
        t={t}
        locale={locale}
      />

      <button
        className={styles.actionButton}
        disabled={!details.can_afford || isPublishing}
        onClick={handlePublish}
      >
        {isPublishing
          ? "Processing..."
          : details.can_afford
            ? "Publish with Credits"
            : "Insufficient Credits"}
      </button>

      {!details.can_afford && (
        <div className={styles.insufficientWrapper}>
          {details.reason_code === "AGENT_LIMIT_EXCEEDED" ? (
            <p className={styles.insufficientText}>
              You have reached your allocated spending limit. Please contact
              your agency admin to allocate more credits to your account.
            </p>
          ) : (
            <p className={styles.insufficientText}>
              Your agency wallet needs{" "}
              {details.credit_cost - details.wallet_balance} more credits.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function PrivatePaymentBox({
  details,
  listingId,
  t,
  locale,
  stripeSuccessUrl,
  cancelUrl,
}: any) {
  const [checked, setChecked] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleStripe = async () => {
    if (!checked) return alert("Please agree to the terms");

    setIsRedirecting(true);
    try {
      // 🚀 STANDALONE CALL: No Store Required
      const res = await fetch("/api/payments/create-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          price_id: details.stripe_price_id,
          quantity: 1, // Listings are always 1 unit
          coupon_code: details.launch_coupon_id, // "D397fyWM"
          listing_id: listingId,
          mode: "subscription", // Listings are recurring
          success_url: `${window.location.origin}${stripeSuccessUrl}`,
          cancel_url: `${window.location.origin}${cancelUrl}`,
        }),
      });

      if (!res.ok) throw new Error("Checkout failed");

      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Stripe Redirect Error:", err);
      alert("Could not initiate payment. Please try again.");
    } finally {
      localStorage.removeItem("pending_listing_draft");
      setIsRedirecting(false);
    }
  };

  return (
    <div className={styles.paymentBox}>
      <TermsAndBilling
        checked={checked}
        setChecked={setChecked}
        t={t}
        locale={locale}
      />

      <button
        className={styles.stripeButton}
        onClick={handleStripe}
        disabled={isRedirecting}
      >
        {isRedirecting ? (
          "Redirecting to Stripe..."
        ) : (
          <>
            <MdCreditCard size={20} />
            Proceed to Secure Payment
          </>
        )}
      </button>
    </div>
  );
}

// TermsAndBilling remains as defined in your prompt...
function TermsAndBilling({
  checked,
  setChecked,
  confirmError,
  setConfirmError,
  t,
  locale,
}: any) {
  return (
    <div className={styles.termsContainer}>
      <div className={styles.billingNotice}>
        <MdAutorenew size={18} className={styles.billingIcon} />
        <span>Billed monthly until stopped. Cancel anytime.</span>
      </div>

      <div className={styles.inputWrapper}>
        <div className={styles.checkBoxWrapper}>
          <input
            id="terms"
            type="checkbox"
            checked={checked}
            onChange={(e) => {
              setChecked(e.target.checked);
              if (e.target.checked && confirmError) {
                setConfirmError(false);
              }
            }}
            className={styles.checkBox}
            required
          />
          <label htmlFor="terms" className={styles.label}>
            {t("termsTitle") || "I agree to the"}{" "}
            <Link
              href={makeLocalizedHrefNoLocale(locale, "terms-of-use")}
              className={styles.link}
              target="_blank"
            >
              {t("terms&Conditions") || "Terms & Conditions"}
            </Link>
            ,{" "}
            <Link
              href={makeLocalizedHrefNoLocale(locale, "refund-policy")}
              className={styles.link}
              target="_blank"
            >
              {t("refundPolicy") || "Refund Policy"}
            </Link>
            , {t("and") || "and"}{" "}
            <Link
              href={makeLocalizedHrefNoLocale(locale, "privacy-policy")}
              className={styles.link}
              target="_blank"
            >
              {t("privacyPolicy") || "Privacy Policy"}
            </Link>
            .
          </label>
        </div>
        {confirmError && (
          <div className={styles.confirmErrorWrapper}>
            <p className={styles.errorText}>
              <MdErrorOutline size={14} />
              {t("confirmError") || "You must agree to the terms to proceed."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function PublishListingLoading() {
  return (
    <main className={styles.mainContainer}>
      <header className={styles.header}>
        <div className={`${styles.skeletonPulse} ${styles.skelTitle}`} />
        <div className={`${styles.skeletonPulse} ${styles.skelDesc}`} />
      </header>

      <div className={styles.layoutGrid}>
        {/* LEFT COLUMN: MOCK LISTING CARD */}
        <div className={styles.leftColumn}>
          <div className={styles.cardPreviewWrapper}>
            <div className={`${styles.skeletonPulse} ${styles.skelCard}`} />
          </div>
          <div className={`${styles.skeletonPulse} ${styles.skelBtn}`} />
        </div>

        {/* RIGHT COLUMN: ORDER SUMMARY */}
        <div className={styles.rightColumn}>
          <div className={styles.checkoutCard}>
            <div
              className={`${styles.skeletonPulse} ${styles.skelCheckoutTitle}`}
            />

            <ul className={styles.summaryList}>
              {[1, 2, 3].map((i) => (
                <li key={i} className={styles.summaryItem}>
                  <div
                    className={`${styles.skeletonPulse} ${styles.skelSummaryItem}`}
                  />
                </li>
              ))}
            </ul>

            <div className={styles.totalRow}>
              <div
                className={`${styles.skeletonPulse} ${styles.skelTotalLabel}`}
              />
              <div
                className={`${styles.skeletonPulse} ${styles.skelTotalAmount}`}
              />
            </div>

            <div className={styles.paymentBox}>
              <div className={`${styles.skeletonPulse} ${styles.skelWallet}`} />
              <div className={`${styles.skeletonPulse} ${styles.skelTerms}`} />
              <div
                className={`${styles.skeletonPulse} ${styles.skelBtnRounded}`}
              />
              <div className={`${styles.skeletonPulse} ${styles.skelSecure}`} />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
